import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import { 
  Film,
  Ticket,
  Star,
  Zap,
  Calendar,
  Users,
  Clock,
  MapPin,
  Trophy,
  Sparkles,
  CheckCircle2,
  Clapperboard,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

const PALETTE = TEMPLATE_PALETTES.studio_action;

const StudioActionTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`.toUpperCase();
    }
    return 'VIP GUEST';
  };

  // Get icon for component
  const getStudioIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    
    if (type.includes('ticket') || title.includes('ticket') || title.includes('admission')) {
      return <Ticket className="w-6 h-6 text-[var(--accent)]" />;
    } else if (type.includes('hotel') || title.includes('hotel') || title.includes('resort')) {
      return <Star className="w-6 h-6 text-[var(--accent)]" />;
    } else if (type.includes('express') || title.includes('express') || title.includes('fast')) {
      return <Zap className="w-6 h-6 text-[var(--accent)]" />;
    } else if (type.includes('dining') || title.includes('dining') || title.includes('restaurant')) {
      return <Trophy className="w-6 h-6 text-[var(--accent)]" />;
    }
    return <Film className="w-6 h-6 text-[var(--accent)]" />;
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Cinematic Header */}
      <div className="relative bg-gradient-to-r from-[var(--primary)] via-[var(--primary)] to-[var(--accent)] overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Film className="w-10 h-10 text-[var(--accent)] animate-pulse" />
            <span className="text-[var(--accent)] font-black uppercase tracking-widest text-sm">VIP Studio Pass</span>
            <Film className="w-10 h-10 text-[var(--accent)] animate-pulse" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black uppercase tracking-widest text-[var(--foreground)] mb-4 drop-shadow-2xl">
            {quote.quoteTitle || 'Your Studio Adventure'}
          </h1>
          
          <div className="inline-block bg-black/50 backdrop-blur-sm px-6 py-3 rounded-lg border-2 border-[var(--accent)]">
            <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-1">Access Code</div>
            <div className="text-[var(--foreground)] font-mono text-xl font-bold">{quote.quoteNumber}</div>
          </div>
        </div>
      </div>

      {/* Guest Information Bar */}
      <div className="bg-[var(--muted)] border-y-2 border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-2 font-bold">VIP Guest</div>
              <div className="text-[var(--foreground)] font-black text-lg uppercase tracking-wide">{getClientName()}</div>
            </div>
            
            {quote.travelDates?.start && (
              <div>
                <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-2 font-bold">Show Dates</div>
                <div className="text-[var(--foreground)] font-black text-lg uppercase tracking-wide">
                  {formatDate(quote.travelDates.start)}
                  {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                </div>
              </div>
            )}
            
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div>
                <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-2 font-bold">Party Size</div>
                <div className="text-[var(--foreground)] font-black text-lg uppercase tracking-wide">
                  {quote.travelers.adults || 0} + {quote.travelers.children?.length || 0}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* VIP Passes / Components */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-black uppercase tracking-widest text-[var(--accent)] mb-2">
            ⚡ Your VIP Access ⚡
          </h2>
          <p className="text-[var(--muted-foreground)] uppercase tracking-wide text-sm">Premium Studio Experiences</p>
        </div>

        {quote.components && quote.components.length > 0 ? (
          <div className="space-y-6">
            {quote.components.map((component, index) => (
              <div
                key={component.id || index}
                className="relative bg-[var(--muted)] rounded-lg overflow-hidden border-2 border-[var(--primary)] shadow-2xl hover:shadow-red-600/50 transition-all"
                style={{
                  clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))'
                }}
              >
                {/* Ticket Perforation Effect */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[var(--muted)] to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[var(--muted)] to-transparent"></div>

                <div className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="mt-1">
                        {getStudioIcon(component)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block px-3 py-1 bg-[var(--primary)] text-[var(--foreground)] text-xs font-black uppercase tracking-widest rounded">
                            Pass #{index + 1}
                          </span>
                          <span className="inline-block px-3 py-1 bg-[var(--muted)] text-[var(--accent)] text-xs font-bold uppercase tracking-wide rounded">
                            {component.type || 'Experience'}
                          </span>
                        </div>
                        <h3 className="text-2xl font-black uppercase tracking-wide text-[var(--foreground)] mb-1">
                          {component.title || `Studio Pass ${index + 1}`}
                        </h3>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-auto">
                      <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-1">Price</div>
                      <div className="text-3xl font-black text-[var(--foreground)]">
                        {formatCurrency(component.price)}
                      </div>
                    </div>
                  </div>

                  {/* Component Details */}
                  {component.data && Object.keys(component.data).length > 0 && (
                    <div className="mt-6 pt-6 border-t-2 border-[var(--border)]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(component.data).map(([key, value]) => {
                          if (!value || key === 'isOption' || key === 'optionDescription') return null;
                          
                          // Check if this is a price field
                          const isPriceField = key.toLowerCase().includes('price') || 
                                               key.toLowerCase().includes('cost') || 
                                               key.toLowerCase().includes('fee') ||
                                               key.toLowerCase().includes('total') ||
                                               key.toLowerCase().includes('deposit');
                          const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                          
                          let displayValue = String(value);
                          if (isPriceField && isNumeric) {
                            displayValue = formatCurrency(parseFloat(value));
                          }
                          
                          return (
                            <div key={key} className="flex items-start gap-2">
                              <Sparkles className="w-4 h-4 text-[var(--accent)] mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="text-xs uppercase tracking-widest text-[var(--accent)] font-bold mb-1">
                                  {humanizeKey(key)}
                                </div>
                                <div className="text-[var(--foreground)] font-semibold">
                                  {displayValue}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Component Photos */}
                  {component.photos && component.photos.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-[var(--accent)]/30">
                      <h4 className="text-sm font-bold text-[var(--accent)] mb-3 uppercase tracking-wider flex items-center gap-2">
                        <span>📸</span> Production Stills
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {component.photos.map((photo, photoIndex) => (
                          <div 
                            key={photoIndex} 
                            className="relative group cursor-pointer overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-800 bg-white"
                            onClick={() => window.open(photo.url, '_blank')}
                          >
                            <img 
                              src={photo.url} 
                              alt={photo.name || 'Component photo'} 
                              className="w-full h-auto object-contain"
                              style={{ maxHeight: '300px' }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                              <span className="text-[var(--foreground)] text-sm font-bold">{photo.name || 'View Photo'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Component Documents */}
                  {component.documents && component.documents.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-[var(--accent)] mb-2 uppercase tracking-wider flex items-center gap-2">
                        <span>📋</span> Production Files
                      </h4>
                      <div className="space-y-2">
                        {component.documents.map((doc, docIndex) => (
                          <a 
                            key={docIndex}
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-[var(--muted)] hover:bg-[var(--muted)] px-4 py-2 rounded transition-colors border border-[var(--accent)]/30"
                          >
                            <span className="text-lg">📄</span>
                            <span className="text-[var(--foreground)] text-sm font-medium">{doc.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasMultipleOptions(component) && (
                    <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                      <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {getComponentOptions(component).map((option, optIndex) => (
                          <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                            {option.photoUrl && (
                              <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                            )}
                            <div className="p-4">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                {option.badge && (
                                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                )}
                              </div>
                              {option.description && (
                                <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                              )}
                              <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[var(--primary)] to-transparent opacity-50"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[var(--muted)] rounded-lg border-2 border-[var(--border)] p-12 text-center">
            <Film className="w-16 h-16 text-[var(--muted-foreground)] mx-auto mb-4" />
            <p className="text-[var(--muted-foreground)] uppercase tracking-wide">No Studio Passes Added</p>
          </div>
        )}

        {/* Special Notes */}
        {quote.notes && (
          <div className="mt-12 bg-[var(--muted)] rounded-lg border-2 border-[var(--accent)] p-6">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-6 h-6 text-[var(--accent)]" />
              <h3 className="text-xl font-black uppercase tracking-widest text-[var(--accent)]">VIP Notes</h3>
            </div>
            <p className="text-[var(--muted-foreground)] leading-relaxed whitespace-pre-wrap">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Total Investment - Cinematic Box */}
        <div className="mt-12 bg-gradient-to-br from-[var(--primary)] via-[var(--primary)] to-[var(--background)] rounded-lg border-4 border-[var(--accent)] shadow-2xl overflow-hidden">
          <div className="bg-black/40 backdrop-blur-sm p-8">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Zap className="w-8 h-8 text-[var(--accent)]" />
                <h3 className="text-3xl font-black uppercase tracking-widest text-[var(--accent)]">
                  Total Investment
                </h3>
                <Zap className="w-8 h-8 text-[var(--accent)]" />
              </div>
              <div className="text-6xl md:text-7xl font-black text-[var(--foreground)] mb-2">
                {formatCurrency(quote.totalPrice)}
              </div>
            </div>

            {quote.depositAmount > 0 && (
              <div className="border-t-2 border-[var(--accent)]/30 pt-6 mb-6">
                <div className="grid md:grid-cols-2 gap-6 text-center">
                  <div>
                    <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-2 font-bold">Initial Deposit</div>
                    <div className="text-3xl font-black text-[var(--foreground)]">{formatCurrency(quote.depositAmount)}</div>
                  </div>
                  {quote.depositDueDate && (
                    <div>
                      <div className="text-[var(--accent)] text-xs uppercase tracking-widest mb-2 font-bold">Due Date</div>
                      <div className="text-2xl font-black text-[var(--foreground)]">{formatDate(quote.depositDueDate)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Button */}
            {onPayment && (
              <div>
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-gradient-to-r from-[var(--primary)] to-[var(--accent)] hover:from-[var(--primary)] hover:to-[var(--accent)] text-[var(--foreground)] font-black py-5 px-8 rounded-lg uppercase tracking-widest text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-2xl hover:shadow-yellow-400/50 hover:scale-105'
                  }`}
                >
                  <CheckCircle2 className="w-6 h-6" />
                  {isAgentPreview ? 'Preview Mode' : 'Secure Your VIP Access'}
                  <Zap className="w-6 h-6" />
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs text-[var(--accent)] mt-4 uppercase tracking-widest">
                    ⚡ Limited Availability • Book Now ⚡
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-[var(--muted)] rounded-lg overflow-hidden shadow-2xl border-2 border-[var(--accent)]">
            <div className="bg-[var(--accent)] px-6 py-3">
              <div className="flex items-center justify-center gap-2 text-[var(--background)]">
                <Film className="w-5 h-5" />
                <h3 className="text-base font-bold uppercase tracking-wider">Your Production Coordinator</h3>
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-[var(--accent)] font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-[var(--muted)] px-4 py-3 rounded hover:bg-[var(--muted)] transition-colors border border-[var(--accent)]/30"
                  >
                    <Mail className="w-5 h-5 text-[var(--accent)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-[var(--muted)] px-4 py-3 rounded hover:bg-[var(--muted)] transition-colors border border-[var(--accent)]/30"
                  >
                    <Phone className="w-5 h-5 text-[var(--accent)]" />
                    <span className="text-[var(--foreground)] font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-[var(--muted-foreground)] text-xs uppercase tracking-widest">
            <Film className="w-4 h-4" />
            <span>Studio Action Travel • Your Adventure Awaits</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioActionTemplate;
